import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FolderOpen, Plus, Trash2, UserRound, Pencil, Loader2, Save, X } from 'lucide-react';
import Drawer from '../components/Drawer';
import Footer from '../components/Footer';
import StyledSelect from '../components/StyledSelect';
import {
    createPerson,
    createProjectCategory,
    deletePerson,
    deleteProjectCategory,
    getPersons,
    getProjectCategories,
    getProjectsList,
    updatePerson,
    updateProjectCategory,
} from '../api/api';
import { useActiveProject } from '../context/ActiveProjectContext';
import { useAuth } from '../context/AuthContext';

const cardStyle = { borderColor: '#f4d6df', background: '#fff' };

export default function CategoryPersonManagementPage() {
    const { canManageCategories, canManagePersons } = useAuth();
    const { selectedProjectId } = useActiveProject();

    if (!canManageCategories && !canManagePersons) {
        const denied = (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
                <div className="rounded-3xl border bg-white p-6 text-center" style={{ borderColor: '#f2d8e0' }}>
                    <h1 className="text-2xl font-bold text-slate-900">Categories & Persons Management</h1>
                    <p className="mt-2 text-sm text-slate-500">You do not have permission to manage categories or persons.</p>
                </div>
            </div>
        );

        return <><Drawer pageHeader={denied} /><Footer /></>;
    }

    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState('');

    const [categories, setCategories] = useState([]);
    const [persons, setPersons] = useState([]);

    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingPersons, setLoadingPersons] = useState(false);

    const [newCategory, setNewCategory] = useState('');
    const [newPerson, setNewPerson] = useState('');

    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [editingCategoryName, setEditingCategoryName] = useState('');

    const [editingPersonId, setEditingPersonId] = useState(null);
    const [editingPersonName, setEditingPersonName] = useState('');

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const projectOptions = useMemo(
        () => projects.map((p) => ({ value: String(p.id), label: p.name })),
        [projects]
    );

    const loadProjects = useCallback(async () => {
        const res = await getProjectsList();
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setProjects(list);

        const candidate = selectedProjectId || (list[0] ? String(list[0].id) : '');
        if (candidate) setProjectId((prev) => prev || String(candidate));
    }, [selectedProjectId]);

    const loadCategories = useCallback(async (pid) => {
        if (!pid) {
            setCategories([]);
            return;
        }

        setLoadingCategories(true);
        try {
            const res = await getProjectCategories(pid);
            setCategories(Array.isArray(res?.data?.data) ? res.data.data : []);
        } catch (err) {
            setCategories([]);
            setError(err?.response?.data?.message || 'Failed to load categories');
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    const loadPersons = useCallback(async () => {
        setLoadingPersons(true);
        try {
            const res = await getPersons();
            setPersons(Array.isArray(res?.data?.data) ? res.data.data : []);
        } catch (err) {
            setPersons([]);
            setError(err?.response?.data?.message || 'Failed to load persons');
        } finally {
            setLoadingPersons(false);
        }
    }, []);

    useEffect(() => {
        loadProjects();
        loadPersons();
    }, [loadProjects, loadPersons]);

    useEffect(() => {
        if (projectId) {
            loadCategories(projectId);
        }
    }, [projectId, loadCategories]);

    const saveCategory = async () => {
        const name = newCategory.trim();
        if (!name || !projectId) return;

        setSaving(true);
        setError('');
        try {
            await createProjectCategory(projectId, { name });
            setNewCategory('');
            await loadCategories(projectId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to create category');
        } finally {
            setSaving(false);
        }
    };

    const savePerson = async () => {
        const name = newPerson.trim();
        if (!name) return;

        setSaving(true);
        setError('');
        try {
            await createPerson({ name });
            setNewPerson('');
            await loadPersons();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to create person');
        } finally {
            setSaving(false);
        }
    };

    const startCategoryEdit = (row) => {
        setEditingCategoryId(row.id);
        setEditingCategoryName(row.name || '');
    };

    const startPersonEdit = (row) => {
        setEditingPersonId(row.id);
        setEditingPersonName(row.name || '');
    };

    const updateCategoryRow = async () => {
        if (!projectId || !editingCategoryId) return;
        const name = editingCategoryName.trim();
        if (!name) return;

        setSaving(true);
        setError('');
        try {
            await updateProjectCategory(projectId, editingCategoryId, { name });
            setEditingCategoryId(null);
            setEditingCategoryName('');
            await loadCategories(projectId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update category');
        } finally {
            setSaving(false);
        }
    };

    const updatePersonRow = async () => {
        if (!editingPersonId) return;
        const name = editingPersonName.trim();
        if (!name) return;

        setSaving(true);
        setError('');
        try {
            await updatePerson(editingPersonId, { name });
            setEditingPersonId(null);
            setEditingPersonName('');
            await loadPersons();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update person');
        } finally {
            setSaving(false);
        }
    };

    const removeCategoryRow = async (id) => {
        if (!projectId || !window.confirm('Delete this category?')) return;

        setSaving(true);
        setError('');
        try {
            await deleteProjectCategory(projectId, id);
            await loadCategories(projectId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete category');
        } finally {
            setSaving(false);
        }
    };

    const removePersonRow = async (id) => {
        if (!window.confirm('Delete this person?')) return;

        setSaving(true);
        setError('');
        try {
            await deletePerson(id);
            await loadPersons();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete person');
        } finally {
            setSaving(false);
        }
    };

    const pageContent = (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28">
            <div className="mb-6 rounded-3xl border p-5 bg-white shadow-sm" style={{ borderColor: '#f2d8e0' }}>
                <h1 className="text-2xl font-bold text-slate-900">Categories & Persons Management</h1>
                <p className="text-sm text-slate-500 mt-1">
                    Manage project categories and reusable persons with real-time database sync.
                </p>
            </div>

            {error ? (
                <div
                    className="mb-4 rounded-xl px-4 py-3 text-sm border-l-4"
                    style={{
                        background: '#fce0e8',
                        color: '#c44569',
                        borderLeftColor: '#c44569',
                    }}
                >
                    {error}
                </div>
            ) : null}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-10">
                {canManageCategories ? <section className="rounded-3xl border p-5 shadow-sm" style={cardStyle}>
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2">
                            <FolderOpen size={18} color="#c44569" />
                            <h2 className="text-lg font-bold text-slate-800">Project Categories</h2>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">
                            Project
                        </label>
                        <StyledSelect
                            value={projectId}
                            onChange={setProjectId}
                            options={projectOptions}
                            placeholder="Select project"
                            searchable
                        />
                    </div>

                    <div className="flex gap-2 mb-4">
                        <input
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            placeholder="Add new category"
                            className="flex-1 h-11 rounded-xl border border-slate-200 px-3 text-sm outline-none focus:ring-4 focus:ring-rose-100 focus:border-rose-300"
                        />
                        <button
                            type="button"
                            onClick={saveCategory}
                            disabled={saving || !projectId}
                            className="h-11 px-4 rounded-xl text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1"
                            style={{ background: '#c44569' }}
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            Add
                        </button>
                    </div>

                    <div className="space-y-2 max-h-[420px] overflow-y-auto">
                        {loadingCategories ? (
                            <p className="text-sm text-slate-400">Loading categories...</p>
                        ) : categories.length === 0 ? (
                            <p className="text-sm text-slate-400">No categories found for this project.</p>
                        ) : (
                            categories.map((row) => (
                                <div
                                    key={row.id}
                                    className="rounded-2xl border border-rose-100 bg-rose-50/40 p-3 flex items-center gap-2"
                                >
                                    {editingCategoryId === row.id ? (
                                        <>
                                            <input
                                                value={editingCategoryName}
                                                onChange={(e) => setEditingCategoryName(e.target.value)}
                                                className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none"
                                            />
                                            <button
                                                type="button"
                                                onClick={updateCategoryRow}
                                                className="px-3 py-2 rounded-lg text-white"
                                                style={{ background: '#16a34a' }}
                                            >
                                                <Save size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingCategoryId(null);
                                                    setEditingCategoryName('');
                                                }}
                                                className="px-3 py-2 rounded-lg text-slate-600 bg-slate-100"
                                            >
                                                <X size={14} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <p className="flex-1 text-sm font-semibold text-slate-700">{row.name}</p>
                                            <button
                                                type="button"
                                                onClick={() => startCategoryEdit(row)}
                                                className="px-3 py-2 rounded-lg text-white"
                                                style={{ background: '#b08800' }}
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeCategoryRow(row.id)}
                                                className="px-3 py-2 rounded-lg text-white"
                                                style={{ background: '#c44569' }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </section> : null}

                {canManagePersons ? <section className="rounded-3xl border p-5 shadow-sm" style={cardStyle}>
                    <div className="flex items-center gap-2 mb-4">
                        <UserRound size={18} color="#0f766e" />
                        <h2 className="text-lg font-bold text-slate-800">Persons</h2>
                    </div>

                    <div className="flex gap-2 mb-4">
                        <input
                            value={newPerson}
                            onChange={(e) => setNewPerson(e.target.value)}
                            placeholder="Add new person"
                            className="flex-1 h-11 rounded-xl border border-slate-200 px-3 text-sm outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-300"
                        />
                        <button
                            type="button"
                            onClick={savePerson}
                            disabled={saving}
                            className="h-11 px-4 rounded-xl text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1"
                            style={{ background: '#0f766e' }}
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            Add
                        </button>
                    </div>

                    <div className="space-y-2 max-h-[420px] overflow-y-auto">
                        {loadingPersons ? (
                            <p className="text-sm text-slate-400">Loading persons...</p>
                        ) : persons.length === 0 ? (
                            <p className="text-sm text-slate-400">No persons available yet.</p>
                        ) : (
                            persons.map((row) => (
                                <div
                                    key={row.id}
                                    className="rounded-2xl border border-teal-100 bg-teal-50/40 p-3 flex items-center gap-2"
                                >
                                    {editingPersonId === row.id ? (
                                        <>
                                            <input
                                                value={editingPersonName}
                                                onChange={(e) => setEditingPersonName(e.target.value)}
                                                className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none"
                                            />
                                            <button
                                                type="button"
                                                onClick={updatePersonRow}
                                                className="px-3 py-2 rounded-lg text-white"
                                                style={{ background: '#16a34a' }}
                                            >
                                                <Save size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingPersonId(null);
                                                    setEditingPersonName('');
                                                }}
                                                className="px-3 py-2 rounded-lg text-slate-600 bg-slate-100"
                                            >
                                                <X size={14} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <p className="flex-1 text-sm font-semibold text-slate-700">{row.name}</p>
                                            <button
                                                type="button"
                                                onClick={() => startPersonEdit(row)}
                                                className="px-3 py-2 rounded-lg text-white"
                                                style={{ background: '#b08800' }}
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removePersonRow(row.id)}
                                                className="px-3 py-2 rounded-lg text-white"
                                                style={{ background: '#c44569' }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </section> : null}
            </div>

            <div className="h-6" />
            <Footer />
        </div>
    );

    return <Drawer pageHeader={pageContent} />;
}